import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import {
  Bell,
  Calendar,
  Clock,
  RefreshCw,
  Play,
  Pause,
  XCircle,
  CheckCircle2,
  Gift,
  Award,
  AlertTriangle,
  FileText,
  Timer,
  Users,
  TrendingUp,
  BarChart3,
  Cake,
  CalendarCheck,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Job type configuration
const jobTypeConfig = {
  birthday: {
    label: 'Cumpleaños',
    icon: Gift,
    color: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300',
    description: '3 días antes y el mismo día'
  },
  anniversary: {
    label: 'Aniversario',
    icon: Award,
    color: 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300',
    description: 'Anual, mismo día de inicio'
  },
  chargeback: {
    label: 'Chargeback',
    icon: AlertTriangle,
    color: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300',
    description: 'Diario, máximo 60 días'
  },
  pending: {
    label: 'Pendiente',
    icon: FileText,
    color: 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300',
    description: 'Semanal hasta cambio'
  },
  renewal: {
    label: 'Renovación',
    icon: Timer,
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300',
    description: '3m, 2m, 1m antes'
  },
  lead_birthday: {
    label: 'Cumpleaños Lead',
    icon: Cake,
    color: 'bg-pink-100 text-pink-800 dark:bg-pink-900 dark:text-pink-300',
    description: 'El mismo día del cumpleaños'
  },
  lead_follow_up: {
    label: 'Seguimiento Lead',
    icon: CalendarCheck,
    color: 'bg-teal-100 text-teal-800 dark:bg-teal-900 dark:text-teal-300',
    description: 'Cuando llega la fecha de contacto'
  }
};

// Status configuration
const statusConfig = {
  active: {
    label: 'Activo',
    icon: Play,
    color: 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300'
  },
  completed: {
    label: 'Completado',
    icon: CheckCircle2,
    color: 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300'
  },
  cancelled: {
    label: 'Cancelado',
    icon: XCircle,
    color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300'
  }
};

export const ScheduledNotificationsPage = () => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';
  
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({
    by_status: { active: 0, completed: 0, cancelled: 0 },
    by_type: {},
    today_notifications: 0,
    total_jobs: 0
  });
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  
  // Filters
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  
  // Detail modal
  const [selectedJob, setSelectedJob] = useState(null);
  const [detailModalOpen, setDetailModalOpen] = useState(false);

  const fetchJobs = useCallback(async () => {
    try {
      const params = {};
      if (statusFilter !== 'all') params.status = statusFilter;
      if (typeFilter !== 'all') params.job_type = typeFilter;
      
      const response = await axios.get(`${API}/notification-jobs`, { params });
      setJobs(response.data);
    } catch (error) {
      console.error('Error fetching jobs:', error);
      if (error.response?.status !== 403) {
        toast.error('Error al cargar jobs de notificaciones');
      }
    }
  }, [statusFilter, typeFilter]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/notification-jobs/stats`);
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchJobs(), fetchStats()]);
      setLoading(false);
    };
    loadData();
  }, [fetchJobs, fetchStats]);

  const handleRunScheduler = async () => {
    setRunning(true);
    try {
      await axios.post(`${API}/notification-jobs/run`);
      toast.success('Procesamiento de notificaciones completado');
      await Promise.all([fetchJobs(), fetchStats()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al ejecutar scheduler');
    } finally {
      setRunning(false);
    }
  };

  const handleCancelJob = async (jobId) => {
    try {
      await axios.put(`${API}/notification-jobs/${jobId}/cancel`);
      toast.success('Job cancelado');
      await Promise.all([fetchJobs(), fetchStats()]);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al cancelar job');
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
      return new Date(dateStr).toLocaleDateString('es-MX', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    } catch {
      return dateStr;
    }
  };

  const openJobDetail = (job) => {
    setSelectedJob(job);
    setDetailModalOpen(true);
  };

  if (!isAdmin) {
    return (
      <div className="flex items-center justify-center h-96">
        <Card>
          <CardContent className="p-8 text-center">
            <AlertTriangle className="h-12 w-12 mx-auto text-yellow-500 mb-4" />
            <h3 className="text-lg font-medium">Acceso Restringido</h3>
            <p className="text-muted-foreground">
              Esta sección solo está disponible para administradores.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="scheduled-notifications-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
            <span>Configuración</span>
            <span>/</span>
            <span className="text-foreground">Sistema de Alertas</span>
          </div>
          <h1 className="text-3xl font-bold tracking-tight">Sistema de Alertas</h1>
          <p className="text-muted-foreground">Gestión de notificaciones automáticas basadas en cron jobs</p>
        </div>
        <Button
          onClick={handleRunScheduler}
          disabled={running}
          className="bg-[#1a365d] hover:bg-[#1a365d]/90"
          data-testid="run-scheduler-btn"
        >
          <RefreshCw className={`h-4 w-4 mr-2 ${running ? 'animate-spin' : ''}`} />
          {running ? 'Ejecutando...' : 'Ejecutar Scheduler'}
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Jobs Activos</p>
                <p className="text-2xl font-bold text-green-600">{stats.by_status.active}</p>
              </div>
              <Play className="h-5 w-5 text-green-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Completados</p>
                <p className="text-2xl font-bold text-blue-600">{stats.by_status.completed}</p>
              </div>
              <CheckCircle2 className="h-5 w-5 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Cancelados</p>
                <p className="text-2xl font-bold text-gray-600">{stats.by_status.cancelled}</p>
              </div>
              <XCircle className="h-5 w-5 text-gray-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Notif. Hoy</p>
                <p className="text-2xl font-bold">{stats.today_notifications}</p>
              </div>
              <Bell className="h-5 w-5 text-yellow-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Total Jobs</p>
                <p className="text-2xl font-bold">{stats.total_jobs}</p>
              </div>
              <BarChart3 className="h-5 w-5 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs text-muted-foreground">Chargebacks</p>
                <p className="text-2xl font-bold text-red-600">{stats.by_type.chargeback || 0}</p>
              </div>
              <AlertTriangle className="h-5 w-5 text-red-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Job Types Legend */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Tipos de Notificación</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-3">
            {Object.entries(jobTypeConfig).map(([key, config]) => {
              const Icon = config.icon;
              return (
                <div key={key} className="flex items-center gap-2 p-2 rounded-lg bg-gray-50 dark:bg-gray-800">
                  <div className={`p-1.5 rounded ${config.color}`}>
                    <Icon className="h-4 w-4" />
                  </div>
                  <div>
                    <p className="text-sm font-medium">{config.label}</p>
                    <p className="text-xs text-muted-foreground">{config.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Filters */}
      <div className="flex gap-4">
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filtrar por estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los estados</SelectItem>
            <SelectItem value="active">Activos</SelectItem>
            <SelectItem value="completed">Completados</SelectItem>
            <SelectItem value="cancelled">Cancelados</SelectItem>
          </SelectContent>
        </Select>
        
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="Filtrar por tipo" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos los tipos</SelectItem>
            <SelectItem value="birthday">Cumpleaños</SelectItem>
            <SelectItem value="anniversary">Aniversario</SelectItem>
            <SelectItem value="chargeback">Chargeback</SelectItem>
            <SelectItem value="pending">Pendiente</SelectItem>
            <SelectItem value="renewal">Renovación</SelectItem>
            <SelectItem value="lead_birthday">Cumpleaños Lead</SelectItem>
            <SelectItem value="lead_follow_up">Seguimiento Lead</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Jobs Table */}
      <Card>
        <CardHeader>
          <CardTitle>Jobs de Notificación</CardTitle>
          <CardDescription>
            Lista de trabajos programados y su estado actual
          </CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-8">
              <Bell className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No hay jobs</h3>
              <p className="text-muted-foreground">
                Ejecuta el scheduler para generar notificaciones programadas
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Tipo</TableHead>
                  <TableHead>Entidad</TableHead>
                  <TableHead>Fecha Objetivo</TableHead>
                  <TableHead>Estado</TableHead>
                  <TableHead>Frecuencia</TableHead>
                  <TableHead>Enviadas</TableHead>
                  <TableHead>Última</TableHead>
                  <TableHead>Acciones</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {jobs.map((job) => {
                  const typeConfig = jobTypeConfig[job.job_type] || {};
                  const TypeIcon = typeConfig.icon || Bell;
                  const statConfig = statusConfig[job.status] || {};
                  const StatIcon = statConfig.icon || Clock;
                  
                  return (
                    <TableRow 
                      key={job.id}
                      className="cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800"
                      onClick={() => openJobDetail(job)}
                    >
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <div className={`p-1.5 rounded ${typeConfig.color || 'bg-gray-100'}`}>
                            <TypeIcon className="h-4 w-4" />
                          </div>
                          <span className="text-sm">{typeConfig.label || job.job_type}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div>
                          <p className="font-medium">{job.entity_name || '-'}</p>
                          <p className="text-xs text-muted-foreground capitalize">{job.entity_type}</p>
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">
                        {job.target_date ? job.target_date.split('T')[0] : '-'}
                      </TableCell>
                      <TableCell>
                        <Badge className={`${statConfig.color || ''} border-0`}>
                          <StatIcon className="h-3 w-3 mr-1" />
                          {statConfig.label || job.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-sm capitalize">
                        {job.frequency === 'daily' ? 'Diario' : 
                         job.frequency === 'weekly' ? 'Semanal' : 
                         job.frequency === 'yearly' ? 'Anual' : job.frequency}
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">{job.notifications_sent}</span>
                        <span className="text-muted-foreground">/{job.max_notifications}</span>
                      </TableCell>
                      <TableCell className="text-sm">
                        {formatDate(job.last_notification_at)}
                      </TableCell>
                      <TableCell>
                        {job.status === 'active' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCancelJob(job.id);
                            }}
                            className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          >
                            <XCircle className="h-4 w-4" />
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Job Detail Modal */}
      <Dialog open={detailModalOpen} onOpenChange={setDetailModalOpen}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle>Detalle del Job</DialogTitle>
            <DialogDescription>
              Información completa del trabajo de notificación
            </DialogDescription>
          </DialogHeader>
          
          {selectedJob && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">Tipo</p>
                  <p className="font-medium">
                    {jobTypeConfig[selectedJob.job_type]?.label || selectedJob.job_type}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Estado</p>
                  <Badge className={`${statusConfig[selectedJob.status]?.color || ''} border-0`}>
                    {statusConfig[selectedJob.status]?.label || selectedJob.status}
                  </Badge>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Entidad</p>
                  <p className="font-medium">{selectedJob.entity_name}</p>
                  <p className="text-xs text-muted-foreground">
                    {selectedJob.entity_type} - {selectedJob.entity_id}
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Fecha Objetivo</p>
                  <p className="font-medium">{selectedJob.target_date?.split('T')[0] || '-'}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Frecuencia</p>
                  <p className="font-medium capitalize">{selectedJob.frequency}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Progreso</p>
                  <p className="font-medium">
                    {selectedJob.notifications_sent} / {selectedJob.max_notifications} notificaciones
                  </p>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground mb-2">Destinatarios</p>
                <div className="flex items-center gap-2">
                  <Users className="h-4 w-4 text-muted-foreground" />
                  <span className="text-sm">{selectedJob.recipients?.length || 0} usuarios</span>
                </div>
              </div>
              
              <div className="border-t pt-4">
                <p className="text-sm text-muted-foreground mb-2">Timestamps</p>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div>
                    <span className="text-muted-foreground">Creado:</span>
                    <span className="ml-2">{formatDate(selectedJob.created_at)}</span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Última notif.:</span>
                    <span className="ml-2">{formatDate(selectedJob.last_notification_at)}</span>
                  </div>
                </div>
              </div>
              
              {selectedJob.metadata && Object.keys(selectedJob.metadata).length > 0 && (
                <div className="border-t pt-4">
                  <p className="text-sm text-muted-foreground mb-2">Metadata</p>
                  <pre className="bg-gray-50 dark:bg-gray-800 p-3 rounded text-xs overflow-auto">
                    {JSON.stringify(selectedJob.metadata, null, 2)}
                  </pre>
                </div>
              )}
              
              {selectedJob.status === 'active' && (
                <div className="border-t pt-4">
                  <Button
                    variant="destructive"
                    onClick={() => {
                      handleCancelJob(selectedJob.id);
                      setDetailModalOpen(false);
                    }}
                    className="w-full"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Cancelar Job
                  </Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ScheduledNotificationsPage;
